import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Check, Edit, ArrowLeft, Send, User, Briefcase, GraduationCap, FileText, Globe, Upload, Shield, Clock, CheckCircle } from 'lucide-react';
import LogoPng from '../../assets/InitX_Logo.png';
import SEO from '../../components/SEO';

const ReviewContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  padding: 20px;
`;

const ReviewForm = styled(motion.div)`
  background: white;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
  border-radius: 16px;
  padding: 0;
  max-width: 1200px;
  margin: 0 auto;
  overflow: hidden;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  color: white;
  padding: 30px 40px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
  }
`;

const Content = styled.div`
  padding: 40px;
`;

const Section = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 25px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    border-radius: 2px;
  }
`;

const SectionTitle = styled.h3`
  color: #1e293b;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
`;

const InfoCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Label = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Value = styled.div`
  font-size: 15px;
  color: #1e293b;
  font-weight: 500;
  line-height: 1.4;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-top: 40px;
  padding-top: 30px;
  border-top: 1px solid #e2e8f0;
`;

const Button = styled.button`
  padding: 14px 28px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &.edit {
    background: #f59e0b;
    color: white;
    
    &:hover {
      background: #d97706;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
    }
  }
  
  &.submit {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
    }
  }
`;

const SkillTag = styled.span`
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 500;
  margin: 4px 6px 4px 0;
  display: inline-block;
  border: 1px solid #93c5fd;
`;

const StatusBadge = styled.div`
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #166534;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #86efac;
`;

const EducationSection = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  
  &.tenth {
    border-left: 4px solid #3b82f6;
  }
  
  &.twelfth {
    border-left: 4px solid #059669;
  }
  
  &.bachelor {
    border-left: 4px solid #dc2626;
  }
`;

const ApplicationReview = () => {
  const [applicationData, setApplicationData] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const appData = urlParams.get('data');
    const jobParam = urlParams.get('job');
    
    if (appData) {
      try {
        setApplicationData(JSON.parse(decodeURIComponent(appData)));
      } catch (error) {
        console.error('Error parsing application data:', error);
      }
    }
    
    if (jobParam) {
      try {
        setJobData(JSON.parse(decodeURIComponent(jobParam)));
      } catch (error) {
        console.error('Error parsing job data:', error);
      }
    }
  }, []);

  const handleEdit = () => {
    window.history.back();
  };

  const handleSubmit = () => {
    const appId = 'INITX-' + Date.now().toString().slice(-6);
    setShowSuccess(true);
    
    setTimeout(() => {
      alert(`✅ Application Submitted Successfully!\n\nApplication ID: ${appId}\nPosition: ${jobData.title}\n\nOur HR team will review your application and contact you within 2-3 business days.\n\nThank you for your interest in InitX!`);
      window.close();
    }, 2500);
  };

  if (!applicationData || !jobData) {
    return (
      <ReviewContainer>
        <div style={{ textAlign: 'center', color: 'white', padding: '100px 0' }}>
          <Clock size={48} style={{ marginBottom: '20px', opacity: 0.7 }} />
          <h2>Loading Application Review...</h2>
        </div>
      </ReviewContainer>
    );
  }

  if (showSuccess) {
    return (
      <ReviewContainer>
        <ReviewForm
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          style={{ textAlign: 'center', maxWidth: '600px', padding: '60px 40px' }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring' }}
            style={{ fontSize: '80px', marginBottom: '20px' }}
          >
            ✅
          </motion.div>
          <h2 style={{ color: '#059669', marginBottom: '15px', fontSize: '28px' }}>Application Submitted Successfully!</h2>
          <p style={{ color: '#64748b', fontSize: '16px', marginBottom: '20px' }}>Processing your application and generating confirmation...</p>
          <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', margin: '20px 0', overflow: 'hidden' }}>
            <motion.div 
              style={{ height: '100%', background: 'linear-gradient(90deg, #059669, #047857)', borderRadius: '4px' }}
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 2.5 }}
            />
          </div>
          <StatusBadge>
            <CheckCircle size={16} />
            Application Confirmed
          </StatusBadge>
        </ReviewForm>
      </ReviewContainer>
    );
  }

  return (
    <ReviewContainer>
      <SEO
        title="Application Review | InitX Technologies"
        description="Review your application with InitX Technologies."
        keywords="application review, InitX Technologies"
        type="website"
        siteName="InitX Technologies"
        noindex={true}
      />
      <ReviewForm
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <Header>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <img src={LogoPng} alt="InitX" style={{ height: '50px', width: 'auto' }} />
                <div>
                  <h1 style={{ fontSize: '28px', fontWeight: '700', margin: '0', color: 'white' }}>InitX Technologies</h1>
                  <p style={{ fontSize: '14px', margin: '0', opacity: 0.9 }}>Application Review Portal</p>
                </div>
              </div>
              <StatusBadge style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>
                <Shield size={16} />
                Secure Review
              </StatusBadge>
            </div>
            
            <div style={{ background: 'rgba(255,255,255,0.15)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)' }}>
              <h2 style={{ color: 'white', fontSize: '24px', fontWeight: '600', marginBottom: '8px' }}>{jobData.title}</h2>
              <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '15px', margin: '0' }}>
                {jobData.department} • {jobData.location} • {jobData.salary}
              </p>
            </div>
          </div>
        </Header>

        <Content>
          {/* Personal Information */}
          <Section>
            <SectionTitle>
              <User size={20} />
              Personal Information
            </SectionTitle>
            <InfoGrid>
              <InfoCard>
                <Label>Full Name</Label>
                <Value>{applicationData.firstName} {applicationData.lastName}</Value>
              </InfoCard>
              <InfoCard>
                <Label>Email Address</Label>
                <Value>{applicationData.email}</Value>
              </InfoCard>
              <InfoCard>
                <Label>Phone Number</Label>
                <Value>{applicationData.phone}</Value>
              </InfoCard>
              <InfoCard>
                <Label>Date of Birth</Label>
                <Value>{applicationData.dateOfBirth || 'Not provided'}</Value>
              </InfoCard>
            </InfoGrid>
          </Section>

          {/* Professional Information */}
          <Section>
            <SectionTitle>
              <Briefcase size={20} />
              Professional Background
            </SectionTitle>
            <InfoGrid>
              <InfoCard>
                <Label>Current Position</Label>
                <Value>{applicationData.currentJobTitle || 'Not specified'}</Value>
              </InfoCard>
              <InfoCard>
                <Label>Current Company</Label>
                <Value>{applicationData.currentCompany || 'Not specified'}</Value>
              </InfoCard>
              <InfoCard>
                <Label>Total Experience</Label>
                <Value>{applicationData.totalExperience}</Value>
              </InfoCard>
              <InfoCard>
                <Label>Expected Salary</Label>
                <Value>{applicationData.expectedSalary}</Value>
              </InfoCard>
            </InfoGrid>
          </Section>

          {/* Education */}
          <Section>
            <SectionTitle>
              <GraduationCap size={20} />
              Academic Qualifications
            </SectionTitle>
            
            <EducationSection className="tenth">
              <h4 style={{ color: '#3b82f6', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>Secondary Education (10th Standard)</h4>
              <InfoGrid>
                <InfoCard style={{ background: '#f8fafc' }}>
                  <Label>Board</Label>
                  <Value>{applicationData.tenth_board}</Value>
                </InfoCard>
                <InfoCard style={{ background: '#f8fafc' }}>
                  <Label>School</Label>
                  <Value>{applicationData.tenth_school}</Value>
                </InfoCard>
                <InfoCard style={{ background: '#f8fafc' }}>
                  <Label>Year</Label>
                  <Value>{applicationData.tenth_year}</Value>
                </InfoCard>
                <InfoCard style={{ background: '#f8fafc' }}>
                  <Label>Percentage</Label>
                  <Value>{applicationData.tenth_percentage}%</Value>
                </InfoCard>
              </InfoGrid>
            </EducationSection>
            
            <EducationSection className="twelfth">
              <h4 style={{ color: '#059669', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>Higher Secondary Education (12th Standard)</h4>
              <InfoGrid>
                <InfoCard style={{ background: '#f8fafc' }}>
                  <Label>Board</Label>
                  <Value>{applicationData.twelfth_board}</Value>
                </InfoCard>
                <InfoCard style={{ background: '#f8fafc' }}>
                  <Label>School/College</Label>
                  <Value>{applicationData.twelfth_school}</Value>
                </InfoCard>
                <InfoCard style={{ background: '#f8fafc' }}>
                  <Label>Year</Label>
                  <Value>{applicationData.twelfth_year}</Value>
                </InfoCard>
                <InfoCard style={{ background: '#f8fafc' }}>
                  <Label>Percentage</Label>
                  <Value>{applicationData.twelfth_percentage}%</Value>
                </InfoCard>
              </InfoGrid>
            </EducationSection>
            
            <EducationSection className="bachelor">
              <h4 style={{ color: '#dc2626', marginBottom: '15px', fontSize: '16px', fontWeight: '600' }}>Bachelor's Degree</h4>
              <InfoGrid>
                <InfoCard style={{ background: '#f8fafc' }}>
                  <Label>University</Label>
                  <Value>{applicationData.bachelor_university}</Value>
                </InfoCard>
                <InfoCard style={{ background: '#f8fafc' }}>
                  <Label>Degree</Label>
                  <Value>{applicationData.bachelor_degree}</Value>
                </InfoCard>
                <InfoCard style={{ background: '#f8fafc' }}>
                  <Label>Year</Label>
                  <Value>{applicationData.bachelor_year}</Value>
                </InfoCard>
                <InfoCard style={{ background: '#f8fafc' }}>
                  <Label>CGPA</Label>
                  <Value>{applicationData.bachelor_cgpa}/10</Value>
                </InfoCard>
              </InfoGrid>
            </EducationSection>
          </Section>

          {/* Skills */}
          <Section>
            <SectionTitle>
              <FileText size={20} />
              Technical Competencies
            </SectionTitle>
            <InfoCard>
              <Label>Primary Skills for {jobData.title}</Label>
              <div style={{ marginTop: '10px' }}>
                {applicationData.primarySkills?.map(skill => (
                  <SkillTag key={skill}>{skill}</SkillTag>
                ))}
              </div>
            </InfoCard>
          </Section>

          {/* Work Preferences */}
          <Section>
            <SectionTitle>
              <Globe size={20} />
              Work Preferences
            </SectionTitle>
            <InfoGrid>
              <InfoCard>
                <Label>Preferred Work Type</Label>
                <Value>{applicationData.workType}</Value>
              </InfoCard>
              <InfoCard>
                <Label>Relocation Willingness</Label>
                <Value>{applicationData.willingToRelocate || 'Not specified'}</Value>
              </InfoCard>
            </InfoGrid>
          </Section>

          {/* Documents */}
          <Section>
            <SectionTitle>
              <Upload size={20} />
              Documents & Portfolio
            </SectionTitle>
            <InfoGrid>
              <InfoCard>
                <Label>Resume/CV</Label>
                <Value>{applicationData.resume ? `📎 ${applicationData.resume.name}` : 'Not uploaded'}</Value>
              </InfoCard>
              <InfoCard>
                <Label>LinkedIn Profile</Label>
                <Value>{applicationData.linkedinProfile || 'Not provided'}</Value>
              </InfoCard>
              <InfoCard>
                <Label>Portfolio/GitHub</Label>
                <Value>{applicationData.portfolio || 'Not provided'}</Value>
              </InfoCard>
            </InfoGrid>
          </Section>

          {/* Cover Letter */}
          {applicationData.coverLetter && (
            <Section>
              <SectionTitle>
                <FileText size={20} />
                Cover Letter
              </SectionTitle>
              <InfoCard>
                <Value style={{ lineHeight: '1.7', fontSize: '14px' }}>{applicationData.coverLetter}</Value>
              </InfoCard>
            </Section>
          )}

          <ButtonGroup>
            <Button className="edit" onClick={handleEdit}>
              <Edit size={18} />
              Edit Application
            </Button>
            <Button className="submit" onClick={handleSubmit}>
              <Send size={18} />
              Submit Application
            </Button>
          </ButtonGroup>

          <div style={{ textAlign: 'center', marginTop: '30px', padding: '20px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #bfdbfe' }}>
            <p style={{ color: '#1e40af', fontSize: '13px', margin: '0', lineHeight: '1.6' }}>
              <Shield size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
              <strong>Secure Submission:</strong> Your application data is encrypted and will be processed according to our privacy policy. 
              Once submitted, modifications cannot be made.
            </p>
          </div>
        </Content>
      </ReviewForm>
    </ReviewContainer>
  );
};

export default ApplicationReview;
